import React, { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import * as R from 'ramda'
import moment from 'moment'
import PropTypes from 'prop-types'
import { DateRange } from 'react-date-range'
import { useTheme } from '@material-ui/core/styles'
import { FormattedMessage } from 'react-intl'
import {
  Chip,
  makeStyles,
  useMediaQuery,
  Icon,
  Typography,
} from '@material-ui/core'

import { getDateFnsLocale, getLocalizedDateRange } from 'commons/date'
import { SUBMIT, CLOSE } from 'app/constants/button'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import customNavigationRenderer from './CustomRangeCalendarToolbar'

const useStyles = makeStyles((theme) => ({
  chip: {
    fontSize: '14px',
    fontWeight: 'normal',
    border: `1px solid ${theme.colors['primary-200']}`,
    backgroundColor: theme.colors['primary-200'],
    color: theme.colors['primary'],

    '&.MuiChip-clickable:hover': {
      border: `1px solid ${theme.colors['primary-350']}`,
      backgroundColor: theme.colors['primary-200'],
    },
    '&.MuiChip-clickable:focus': {
      border: `1px solid ${theme.colors['primary-350']}`,
      backgroundColor: theme.colors['primary-200'],
    },
  },
  rootDateRangPicker: {
    '& .rdrDateDisplayWrapper': { display: 'none' },
    '& .rdrDay:not(.rdrDayPassive), .rdrWeekDay, .rdrMonthName, .rdrMonthAndYearPickers select':
      {
        color: theme.colors['primary'],
      },
    '& .rdrDayToday .rdrDayNumber span:after': {
      backgroundColor: theme.colors['primary'],
    },
    '& .rdrNextPrevButton': {
      backgroundColor: theme.colors['primary-350'],
    },
    '& .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span,.rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span,.rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span,.rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span ':
      {
        color: 'rgba(255, 255, 255)',
        fontWeight: 500,
      },
  },
}))

const CalendarSelector = (props) => {
  const {
    name = 'CalendarSelector',
    values = { startDate: new Date(), endDate: new Date(), key: 'selection' },
    className = ' text-14 p-5',
    label = 'Selector Lable',
    onChange = () => {},
    disabled = false,
    ...rest
  } = props

  const theme = useTheme()
  const classes = useStyles()
  const isDownSM = useMediaQuery(theme.breakpoints.down('sm'))

  const [isOpen, setIsOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState({})

  const today = moment().endOf('day').toDate()

  const selectedDatePicker = (date) => {
    onChange(date)
    setIsOpen(false)
  }

  const handleDateRangLable = useCallback(
    (selectedDate) => {
      if (R.isNil(values) && !isOpen) return ''
      else
        return (
          <Typography>
            :
            <strong className="mx-3">
              {getLocalizedDateRange(
                selectedDate.startDate,
                selectedDate.endDate,
              )}
            </strong>
          </Typography>
        )
    },
    [isOpen, values],
  )

  const handleDateRang = (date) => {
    if (moment(date.startDate).isSame(date.endDate))
      selectedDatePicker({
        ...date,
        startDate: moment(date.startDate).startOf('day').toDate(),
        endDate: moment(date.endDate).endOf('day').toDate(),
      })
    else selectedDatePicker(date)
  }

  useEffect(() => {
    setSelectedDate(
      values ?? {
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
      },
    )
  }, [values])

  return (
    <>
      <Chip
        className={clsx(classes.chip, className)}
        label={
          <div className="flex">
            <div className="flex items-center">
              {label}
              {handleDateRangLable(selectedDate)}
            </div>
            <Icon fontSize="small" className="text-primary">
              {isOpen ? 'expand_less' : 'expand_more'}
            </Icon>
          </div>
        }
        disabled={disabled}
        onClick={() => {
          setIsOpen(true)
        }}
      />

      {isOpen ? (
        <div className="bg-white border-1 p-3 rounded-8 absolute left-0 mt-40 mx-10 z-99">
          <DateRange
            {...rest}
            locale={getDateFnsLocale()}
            name={name}
            onChange={(item) => {
              setSelectedDate(item.selection)
            }}
            moveRangeOnFirstSelection={false}
            months={isDownSM ? 1 : 2}
            monthDisplayFormat="MMMM"
            maxDate={today}
            ranges={[selectedDate]}
            rangeColors={['#183560']}
            direction="horizontal"
            className={classes.rootDateRangPicker}
            navigatorRenderer={customNavigationRenderer}
          />
          <div className="flex justify-end mb-5">
            <OutlinedButtons
              color={CLOSE}
              onClick={() => {
                setSelectedDate(
                  values ?? {
                    startDate: new Date(),
                    endDate: new Date(),
                    key: 'selection',
                  },
                )
                setIsOpen(false)
              }}
            >
              <FormattedMessage id="action.cancel" />
            </OutlinedButtons>
            <ContainedButton
              color={SUBMIT}
              className="mx-3"
              onClick={() => handleDateRang(selectedDate)}
            >
              <FormattedMessage id="action.submit" />
            </ContainedButton>
          </div>
        </div>
      ) : null}
    </>
  )
}

CalendarSelector.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  values: PropTypes.object,
  isOneSelected: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
}

export default CalendarSelector
